import React, { useState, useEffect, useRef } from 'react';
import MapComponent from '../MapComponent';
import { authService } from '../../services/authService';

const AppointmentSection = ({ formData, updateFormData, sinCitaMode, onSinCitaToggle }) => {
  const [disponibilidad, setDisponibilidad] = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  // Mapa
  const [clientLocation,    setClientLocation]    = useState(null);
  const [ordenesGeo,        setOrdenesGeo]        = useState([]);
  const [loadingMapa,       setLoadingMapa]       = useState(false);
  const [citaActivaMapa,    setCitaActivaMapa]     = useState(null);
  const geocodedCP = useRef(null);

  useEffect(() => {
    if (formData.codigoPostal) {
      loadDisponibilidad();
      geocodeClientCP(formData.codigoPostal);
    }
  }, []); // eslint-disable-line

  const geocodeClientCP = async (cp) => {
    if (geocodedCP.current === cp) return;
    try {
      const res  = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cp + ', España')}&limit=1`,
        { headers: { 'User-Agent': 'ClimatizacionZapata/1.0 (contacto@climatizacionzapata.es)' } }
      );
      const data = await res.json();
      if (data.length > 0) {
        geocodedCP.current = cp;
        setClientLocation({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), cp });
      }
    } catch {}
  };

  const loadDisponibilidad = async () => {
    setLoading(true);
    setError('');
    setDisponibilidad(null);
    try {
      const res  = await fetch(`/.netlify/functions/getDisponibilidad?cp=${formData.codigoPostal}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.error || data.tecnico === 'Sin asignar') {
        setError(data.error || 'Código postal no encontrado en nuestras zonas');
        return;
      }
      setDisponibilidad(data);
    } catch (e) {
      setError(`Error conectando: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const cargarOrdenesDelDia = async (cita) => {
    setCitaActivaMapa(cita);
    setLoadingMapa(true);
    setOrdenesGeo([]);
    try {
      const res  = await fetch(`/.netlify/functions/geocodificarCita?fecha=${cita.fechaISO}&tecnico=${cita.tecnico}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      setOrdenesGeo(data.ordenes || []);
    } catch {}
    finally { setLoadingMapa(false); }
  };

  const selectCita = (cita) => {
    onSinCitaToggle(false);
    updateFormData({ citaSeleccionada: cita });
    cargarOrdenesDelDia(cita);
  };

  const toggleSinCita = () => {
    if (sinCitaMode) {
      onSinCitaToggle(false);
    } else {
      onSinCitaToggle(true);
      updateFormData({ citaSeleccionada: null });
    }
  };

  // Centro del mapa
  const mapCenter = (() => {
    if (citaActivaMapa && ordenesGeo.length > 0) {
      const pts = ordenesGeo.filter(o => o.lat && o.lng);
      if (pts.length > 0) {
        const all = clientLocation ? [clientLocation, ...pts] : pts;
        return { lat: all.reduce((s, p) => s + p.lat, 0) / all.length, lng: all.reduce((s, p) => s + p.lng, 0) / all.length };
      }
    }
    return clientLocation || { lat: 37.9922, lng: -1.1307 };
  })();

  return (
    <div className="px-4 py-3 space-y-2.5">

      {/* Info de zona */}
      {disponibilidad && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
          <span><span className="font-semibold">Zona:</span> {disponibilidad.zona}</span>
          <span className="text-blue-300">·</span>
          <span><span className="font-semibold">Técnico:</span> {disponibilidad.tecnicoPrincipal}</span>
          <span className="text-blue-300">·</span>
          <span className="text-blue-500">{disponibilidad.allWorkorders?.length || 0}/80 esta semana</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-2 text-sm text-gray-400">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin flex-shrink-0" />
          Cargando disponibilidad…
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-700">
          <span className="flex-1">{error}</span>
          <button onClick={loadDisponibilidad} className="font-semibold underline flex-shrink-0">Reintentar</button>
        </div>
      )}

      {/* Slots + Mapa */}
      {(disponibilidad || error) && !loading && (
        <div className="flex gap-3" style={{ minHeight: 260 }}>

          {/* Lista de slots */}
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            {disponibilidad?.citas?.length > 0 && disponibilidad.citas.map((cita, i) => {
              const selected = formData.citaSeleccionada?.fechaISO === cita.fechaISO;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectCita(cita)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-left transition-all ${
                    selected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold leading-tight truncate ${selected ? 'text-emerald-800' : 'text-gray-800'}`}>
                      {cita.dia}
                      {cita.instalacion && <span className="ml-1 font-normal text-red-500">⚠</span>}
                    </p>
                    <p className={`text-xs mt-0.5 truncate ${selected ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {cita.tecnico}{cita.apoyo ? ' (apoyo)' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-lg ${
                      selected ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {cita.huecos} huecos
                    </span>
                    {selected && <span className="text-emerald-500 text-xs font-bold">✓</span>}
                  </div>
                </button>
              );
            })}

            {disponibilidad?.citas?.length === 0 && (
              <div className="text-center py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                No hay citas disponibles en los próximos días.
              </div>
            )}

            <div className="flex items-center gap-2 py-0.5 mt-auto">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 font-medium px-1">o</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <button
              type="button"
              onClick={toggleSinCita}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border-2 text-xs font-semibold transition-all ${
                sinCitaMode
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span>📋</span>
                <span>Sin cita — incidencia en gestión</span>
              </span>
              {sinCitaMode && (
                <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full flex-shrink-0">✓</span>
              )}
            </button>
          </div>

          {/* Mapa Google Maps */}
          <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-gray-200" style={{ minHeight: 260 }}>
            {loadingMapa ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400 text-xs gap-2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
                Cargando órdenes…
              </div>
            ) : (
              <MapComponent
                center={mapCenter}
                markers={ordenesGeo}
                clientMarker={clientLocation}
                loading={false}
                showLegend={false}
              />
            )}
            {!citaActivaMapa && !loadingMapa && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {/* hint text is handled by MapComponent itself */}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentSection;
